// ---------------------------------------------------------------------------
// PWA Caisse Tablette — useReservations hook
// Provides access to reservations from the Zustand store and exposes
// search and table-lookup helpers from the reservation sync service.
// ---------------------------------------------------------------------------

import { useEffect, useCallback } from 'react';
import { useReservationStore } from '@/store/reservation-store';
import { reservationSync } from '@/services/reservation-sync';
import type { Reservation } from '@/lib/types';

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

interface UseReservationsReturn {
  /** All reservations from the Zustand store. */
  reservations: Reservation[];
  /** Search reservations locally by name, phone, QR code, or ID. */
  search: (query: string) => Reservation[];
  /** Get reservations assigned to a specific table. */
  getByTable: (tableId: string) => Reservation[];
  /** Manually re-sync reservations from the backend. */
  refresh: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook that syncs reservations on mount and provides search functionality.
 * Reads from the Zustand reservation store (updated by reservation-sync).
 */
export function useReservations(): UseReservationsReturn {
  const reservations = useReservationStore((s) => s.reservations);

  useEffect(() => {
    // Sync from server on mount
    reservationSync.sync().catch(() => {});
  }, []);

  const search = useCallback((query: string): Reservation[] => {
    return reservationSync.search(query);
  }, []);

  const getByTable = useCallback((tableId: string): Reservation[] => {
    return reservationSync.getByTable(tableId);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    await reservationSync.sync();
  }, []);

  return { reservations, search, getByTable, refresh };
}
